import { createFileRoute } from '@tanstack/react-router';
import { RouterLinkAs } from '@/lib/router-link';
import {
  I,
  Icon,
  ListItem,
  List,
  PageHeader,
  Screen,
  ScreenBody,
  SettingIcon,
} from '@leanin/ui';
import type { IconName, Tone } from '@leanin/ui';
import { SignOutRow } from './-components/SignOutRow';

type Section = {
  readonly id: string;
  readonly icon: IconName;
  readonly tone: Tone;
  readonly label: string;
  readonly description: string;
  readonly href: string;
};

const SECTIONS: readonly Section[] = [
  {
    id: 'profile',
    icon: 'users',
    tone: 'burgundy',
    label: 'Profile and identity',
    description:
      'Update your name, photo, bio, and email address so other members can get to know you.',
    href: '/settings/account',
  },
  {
    id: 'notifications',
    icon: 'bell',
    tone: 'emerald',
    label: 'Notifications',
    description:
      'Choose which notifications also reach your inbox. In-app notifications always appear in your feed.',
    href: '/settings/notifications',
  },
  {
    id: 'email',
    icon: 'bell',
    tone: 'yellow',
    label: 'Marketing emails',
    description:
      'Newsletters, event invites, and partner stories from Lean In. Transactional notifications are managed under Notifications.',
    href: '/settings/email',
  },
  {
    id: 'privacy',
    icon: 'lock',
    tone: 'navy',
    label: 'Privacy and visibility',
    description:
      'Manage who can see your profile, activity, and Circles. Your account is private by default.',
    href: '/settings/privacy',
  },
  {
    id: 'sessions',
    icon: 'shield',
    tone: 'emerald',
    label: 'Login and devices',
    description:
      'Review where you\'re signed in and revoke any session you don\'t recognize.',
    href: '/settings/sessions',
  },
  {
    id: 'connected',
    icon: 'network',
    tone: 'neutral',
    label: 'Connected accounts',
    description:
      'Link a Google account so you can sign in faster next time.',
    href: '/settings/connected',
  },
  {
    id: 'dsr',
    icon: 'book',
    tone: 'neutral',
    label: 'Data and downloads',
    description:
      'Request a copy of your data, update your information, or delete your account. We respond to requests within 30 days.',
    href: '/settings/dsr',
  },
];

export const Route = createFileRoute('/_authed/settings/')({
  component: SettingsHubPage,
});

function SettingsHubPage() {
  return (
    <Screen>
      <ScreenBody gap="2xl">
        <PageHeader
          title="Settings"
          subtitle="Manage your account, privacy, notifications, and preferences."
        />
        <List>
          {SECTIONS.map((s) => (
            <ListItem
              key={s.id}
              linkAs={RouterLinkAs}
              href={s.href}
              leading={<SettingIcon tone={s.tone}>{I[s.icon]}</SettingIcon>}
              title={s.label}
              subtitle={s.description}
              trailing={<Icon size={18}>{I.arrow}</Icon>}
            />
          ))}
        </List>

        <List>
          <ListItem
            linkAs={RouterLinkAs}
            href="/help"
            leading={<SettingIcon tone="neutral">{I.globe}</SettingIcon>}
            title="Help and support"
            subtitle="Get help with your account, review community guidelines, or contact our support team."
            trailing={<Icon size={18}>{I.arrow}</Icon>}
          />
          <SignOutRow />
        </List>
      </ScreenBody>
    </Screen>
  );
}
